import { ChangeDetectorRef, Component, Input, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import type { BillingPlanTier } from '@recruitee/products-types';

import { FeatureGuardFacade } from '../feature-guard.facade';
import { FeatureData, FeaturesGuardModel } from '../feature-guard.model';
import { FeatureGuardContentComponent } from '../feature-guard.module';
import { GuardedFeature } from '../feature-guard.types';

@Component({
  standalone: true,
  imports: [FeatureGuardContentComponent],
  selector: 'rt-feature-guard-empty',
  templateUrl: './feature-guard-empty.component.html',
  styleUrls: ['./feature-guard-empty.component.less'],
})
export class FeatureGuardEmptyComponent {
  public featureData: FeatureData;
  public canUpgrade: boolean = false;
  public paddingTopLarge: boolean = false;
  public availableFromPlan: BillingPlanTier | null;

  @Input() public customUpgradeButton: TemplateRef<any> | undefined;

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private model: FeaturesGuardModel,
    private router: Router,
    private route: ActivatedRoute,
    private guardFacade: FeatureGuardFacade,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnInit() {
    const feature: GuardedFeature = this.route.snapshot.data.feature;
    const paddingTopLarge: boolean = this.route.snapshot.data.paddingTopLarge;

    if (feature) {
      this.featureData = this.model.data[feature];
      this.store
        .select(AbilitiesSelectors.hasOne(Ability.ManageSubscription))
        .pipe(first())
        .subscribe(canManageSubscription => {
          this.canUpgrade = canManageSubscription;
        });

      this.guardFacade
        .getPlanWhereFeatureIsAvailable(feature)
        .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
        .subscribe(availableFromPlan => {
          this.availableFromPlan = availableFromPlan;
          this.cdRef.detectChanges();
        });

      if (this.featureData.analytics) {
        this.featureData.analytics.discrimination();
      }
    }

    this.paddingTopLarge = paddingTopLarge || false;
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public openPlanPicker() {
    this.router.navigate([{ outlets: { dialog: ['order-2024', 'select-product'] } }]);

    if (this.featureData.analytics) {
      this.featureData.analytics.cta();
    }
  }
}
